import { scenario, scenarios, demoProfile, escapeHTML as E } from "./demo";
import { analyzeBatch, csv } from "./engine/engine";
import { safeJSON } from "./engine/jsonld";
import { validateProfile } from "./engine/profile";
import {
  LABELS,
  LIMITS,
  type Input,
  type Profile,
  type Report,
  type Finding,
  type PageReport,
} from "./engine/types";
import { corpusInputs } from "./corpus";
const $ = <T extends HTMLElement = HTMLElement>(id: string) =>
  document.getElementById(id)! as T;
const value = (id: string) => $<HTMLInputElement>(id).value;
const announce = (s: string) => {
  $("live").textContent = s;
};
const showError = (s: string) => {
  $("profile-error").hidden = false;
  $("profile-error").textContent = s;
};
const clearError = () => {
  $("profile-error").hidden = true;
  $("profile-error").textContent = "";
};
let profile: Profile = structuredClone(demoProfile),
  inputs: Input[] = [],
  report: Report | null = null,
  currentScenario = scenario("S1"),
  demoEpoch = 0,
  job = 0,
  active = false,
  ready = false,
  loading = false,
  profileDirty = false,
  ingestEpoch = 0,
  replayId = "S1",
  stale = false,
  limit = 100,
  sortReverse = false,
  lastProofButton: HTMLButtonElement | null = null;
let worker: Worker;
const objectUrls = new Set<string>();
function startWorker() {
  ready = false;
  worker = new Worker(new URL("./worker.js", import.meta.url), {
    type: "module",
  });
  worker.onmessage = (e) => {
    const m = e.data;
    if (m.type === "ready") {
      ready = true;
      updateAnalyze();
      return;
    }
    if (m.id !== job) return;
    if (m.type === "progress") {
      $<HTMLProgressElement>("progress").value = m.done;
      $<HTMLProgressElement>("progress").max = m.total;
      if (m.done === m.total || m.done % 20 === 0)
        announce(`${m.done} / ${m.total} fichiers examinés.`);
      return;
    }
    active = false;
    setRunning(false);
    if (m.type === "error") {
      showError(m.message);
      announce("Analyse impossible. Consultez le message d’erreur.");
      return;
    }
    clearError();
    if (m.type === "preview") {
      renderPreview(m.result);
      announce("Prévisualisation du témoin disponible.");
      return;
    }
    if (m.type === "report") {
      report = m.report;
      stale = false;
      limit = 100;
      renderReport();
      announce(
        `Analyse terminée : ${report!.summary.pages} pages, ${report!.summary.differences} avec écart, ${report!.summary.incomplete} avec contrôle incomplet.`,
      );
    }
  };
  worker.onerror = () => {
    active = false;
    setRunning(false);
    showError(
      "Le worker n’a pas pu démarrer. Rechargez la page avec ses ressources locales disponibles.",
    );
  };
}
function updateAnalyze() {
  $<HTMLButtonElement>("analyze").disabled =
    !inputs.length || !ready || active || loading || profileDirty;
  $<HTMLButtonElement>("preview").disabled = active || loading || profileDirty;
}
function setRunning(on: boolean) {
  $("cancel").hidden = !on;
  $("progress-area").hidden = !on;
  $<HTMLButtonElement>("preview").disabled = on;
  updateAnalyze();
}
function invalidate(
  message = "Le profil ou les fichiers ont changé. Relancez l’analyse.",
) {
  if (active) {
    job++;
    worker.terminate();
    active = false;
    startWorker();
    setRunning(false);
  }
  clearError();
  $("preview-result").hidden = true;
  if (report) {
    stale = true;
    $("report-state").textContent = "Rapport obsolète";
    $("report-summary").textContent = message;
    $("results").replaceChildren();
    $("proof").hidden = true;
    $("visible-count").textContent =
      "Les anciens constats sont retirés jusqu’à une nouvelle analyse.";
    $<HTMLButtonElement>("export-json").disabled = true;
    $<HTMLButtonElement>("export-csv").disabled = true;
  }
  announce(message);
  updateAnalyze();
}
function updateFiles() {
  const bytes = inputs.reduce(
    (s, i) => s + new TextEncoder().encode(i.html).length,
    0,
  );
  $("file-summary").textContent = inputs.length
    ? `${inputs.length} fichier${inputs.length > 1 ? "s" : ""} · ${(bytes / 1000).toFixed(1)} ko · témoin : ${inputs[0].name}`
    : "Aucun fichier sélectionné.";
  updateAnalyze();
}
function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  objectUrls.add(url);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    objectUrls.delete(url);
  }, 1000);
}
function sourceView() {
  const key = value("source-view");
  const content =
    key === "htmlA"
      ? currentScenario.inputs[0].html
      : key === "htmlB"
        ? currentScenario.inputs[1].html
        : currentScenario[key as keyof typeof currentScenario];
  $("source-code").textContent =
    typeof content === "string" ? content : JSON.stringify(content, null, 2);
}
function displayValue(v: any): string {
  if (typeof v === "string") return v;
  if (Array.isArray(v))
    return v
      .map((x) =>
        typeof x === "string"
          ? x
          : x.question
            ? `${x.question}\n${typeof x.answer === "string" ? x.answer : x.answer.text}`
            : x.name
              ? `${x.name}${x.url ? " → " + x.url : ""}`
              : JSON.stringify(x),
      )
      .join("\n");
  return JSON.stringify(v);
}
async function renderScenario(id: string) {
  const epoch = ++demoEpoch;
  currentScenario = scenario(id);
  const { info, htmlRecord } = currentScenario;
  $("comparison").setAttribute("aria-busy", "true");
  $<HTMLSelectElement>("scenario").value = info.id;
  $("scenario-heading").textContent = info.name;
  $("scenario-explanation").textContent = info.explanation;
  $("scenario-number").textContent = info.id;
  $("scenario-lesson").textContent = info.lesson;
  $("apply-scenario").textContent =
    info.id === "S0"
      ? (scenarios.find((s) => s.id === replayId) || scenarios[1]).action
      : "Rejouer : voir l’état initial";
  $<HTMLAnchorElement>("share-scenario").href =
    `?scenario=${info.id}#comprendre`;
  const results = await analyzeBatch(currentScenario.inputs, demoProfile);
  if (epoch !== demoEpoch) return;
  $("comparison").innerHTML = results.pages
    .map((p, i) => {
      const f = p.findings.find((f) => f.rule_id === info.field)!;
      const h =
        info.field === "modified"
          ? (f.htmlRaw as any).text
          : displayValue(f.htmlNormalized);
      const j =
        info.field === "modified"
          ? String(f.jsonRaw)
          : displayValue(f.jsonNormalized);
      const good = f.status === "concordant";
      return `<article class="comparison-card ${i ? "shared" : ""}"><header class="card-top"><div><span class="letter">Architecture ${i ? "B" : "A"}</span><h3>${i ? "Source éditoriale commune" : "Saisies séparées"}</h3></div><span class="architecture-dot" aria-hidden="true"></span></header><div class="card-body"><div class="card-caption"><span>ARTICLE FICTIF</span><span>HTML révision ${htmlRecord.revision} / JSON révision ${i ? currentScenario.bRecord.revision : currentScenario.aRecord.revision}</span></div><h4 class="fiction-title">${E(htmlRecord.title)}</h4><p class="fiction-body">${E(htmlRecord.body)}</p><dl class="value-pair"><div><dt>Dans la page</dt><dd>${E(h)}</dd></div><div class="marked"><dt>Dans le JSON-LD</dt><dd>${E(j)}</dd></div></dl><div class="verdict ${good ? "good" : ""}"><strong>${good ? "✓ Concordance observée" : "≠ Écart détecté"}</strong><p>${E(f.reason)}</p></div><div class="card-foot"><span>${E(f.label)} · ${E(f.rule_id)}</span><span>${p.scripts} script JSON valide · ${p.findings.length} contrôles</span></div></div></article>`;
    })
    .join("");
  $("comparison").setAttribute("aria-busy", "false");
  sourceView();
}
function fillProfile() {
  profileDirty = false;
  for (const [id, v] of Object.entries({
    "profile-id": profile.id,
    timezone: profile.timezone || "",
    marker: profile.marker,
    "page-url-selector": profile.pageUrl?.selector || "",
  }))
    $<HTMLInputElement>(id).value = v;
  const article = profile.rules.find(
    (r) => r.reader === "date" || r.reader === "authors",
  )?.entity;
  $<HTMLSelectElement>("entity-by").value = article?.by || "page_url";
  $<HTMLInputElement>("entity-id").value = article?.id || "";
  $<HTMLInputElement>("entity-id").disabled = article?.by !== "id";
  $("rule-fields").innerHTML = profile.rules
    .map(
      (r, i) =>
        `<fieldset><legend>${E(r.label)}</legend><label for="rule-${i}-scope">Périmètre HTML<input id="rule-${i}-scope" data-rule="${i}" data-field="scope" value="${E(r.scope)}"></label><label for="rule-${i}-selector">Sélecteur des éléments<input id="rule-${i}-selector" data-rule="${i}" data-field="selector" value="${E(r.selector)}"></label>${Object.entries(
          r.fields || {},
        )
          .map(
            ([k, v]) =>
              `<label for="rule-${i}-${k}">${E({ name: "Nom / libellé", url: "Lien", question: "Question", answer: "Réponse", keyAttribute: "Attribut de la clé stable" }[k] || k)}<input id="rule-${i}-${k}" data-rule="${i}" data-field="fields.${k}" value="${E(v)}"></label>`,
          )
          .join(
            "",
          )}${r.reader === "date" ? `<label for="rule-${i}-format">Format de la date textuelle<select id="rule-${i}-format" data-rule="${i}" data-field="policy.dateFormat"><option value="fr" ${r.policy.dateFormat === "fr" ? "selected" : ""}>ISO ou jour mois français année</option><option value="iso" ${r.policy.dateFormat === "iso" ? "selected" : ""}>ISO uniquement</option></select></label>` : ""}${["faq", "breadcrumb"].includes(r.reader) ? `<label for="rule-${i}-entity">@id de l’entité JSON-LD<input id="rule-${i}-entity" data-rule="${i}" data-field="entity.id" value="${E(r.entity.id || "")}"></label>` : ""}</fieldset>`,
    )
    .join("");
  syncJson();
}
function syncJson() {
  $<HTMLTextAreaElement>("profile-json").value = JSON.stringify(
    profile,
    null,
    2,
  );
}
function formChange() {
  profile.id = value("profile-id");
  profile.marker = value("marker");
  if (value("timezone")) profile.timezone = value("timezone");
  else delete profile.timezone;
  if (value("page-url-selector"))
    profile.pageUrl = {
      selector: value("page-url-selector"),
      attribute: "href",
    };
  else delete profile.pageUrl;
  for (const r of profile.rules.filter((r) =>
    ["date", "authors"].includes(r.reader),
  )) {
    r.entity.by = value("entity-by") as any;
    if (r.entity.by === "id") r.entity.id = value("entity-id");
    else delete r.entity.id;
  }
  $<HTMLInputElement>("entity-id").disabled = value("entity-by") !== "id";
  syncJson();
  invalidate();
}
function currentInputs(): Input[] {
  return inputs.map((input, i) => ({
    ...input,
    mode: value("input-mode") as Input["mode"],
    ...(i === 0 && value("reference-url")
      ? { referenceUrl: value("reference-url") }
      : { referenceUrl: input.referenceUrl }),
    ...(value("captured-at")
      ? { capturedAt: value("captured-at") }
      : { capturedAt: undefined }),
    provenance: input.provenance.startsWith("Corpus fictif")
      ? input.provenance
      : value("provenance"),
  }));
}
function run(kind: "preview" | "analyze") {
  try {
    clearError();
    if (profileDirty || loading)
      throw new Error(
        "Appliquez le profil JSON ou attendez la fin de l’import.",
      );
    validateProfile(profile);
    if (!inputs.length)
      throw new Error("Importez une page ou chargez l’exemple.");
    if (!ready)
      throw new Error("Le moteur se prépare. Réessayez dans un instant.");
    job++;
    active = true;
    setRunning(true);
    $("progress-area").hidden = kind === "preview";
    $<HTMLProgressElement>("progress").value = 0;
    worker.postMessage({
      id: job,
      kind,
      profile,
      inputs:
        kind === "preview" ? currentInputs().slice(0, 1) : currentInputs(),
    });
    announce(
      kind === "preview" ? "Extraction du témoin…" : "Analyse locale en cours…",
    );
  } catch (e: any) {
    showError(e.message);
    announce("Configuration à corriger.");
  }
}
function renderPreview(values: any[]) {
  $("preview-result").hidden = false;
  $("preview-values").innerHTML = values
    .map(
      (v) =>
        `<div class="preview-item"><p><strong>${E(v.label)}</strong> · ${v.count} élément(s), ${v.scopeCount} périmètre(s) ${v.ambiguous ? "— sélection absente ou ambiguë" : ""}</p><pre tabindex="0" aria-label="Valeurs extraites pour ${E(v.label)}">${E(JSON.stringify(v.values, null, 2))}</pre></div>`,
    )
    .join("");
}
const ranks = {
  ecart: 0,
  invalide: 1,
  non_determine: 2,
  non_pris_en_charge: 3,
  non_applicable: 4,
  concordant: 5,
};
function renderReport() {
  if (!report) return;
  $("report").hidden = false;
  $("report-state").textContent = "Analyse à jour";
  $<HTMLButtonElement>("export-json").disabled = false;
  $<HTMLButtonElement>("export-csv").disabled = false;
  const s = report.summary;
  $("report-summary").textContent =
    `${s.pages} pages examinées · ${s.differences} avec au moins un écart · ${s.incomplete} avec contrôle incomplet · ${s.concordant} concordantes sur le profil.`;
  $("proof").hidden = true;
  renderRows();
}
function renderRows() {
  if (!report || stale) return;
  const rows = report.pages.flatMap((p) => p.findings.map((f) => ({ p, f })));
  const query = value("search-file").toLowerCase(),
    filter = value("filter");
  const filtered = rows.filter(
    ({ p, f }) =>
      p.file.toLowerCase().includes(query) &&
      (filter === "all" || filter === "attention"
        ? !["concordant", "non_applicable"].includes(f.status) ||
          filter === "all"
        : f.status === filter),
  );
  filtered.sort(
    (a, b) =>
      (ranks[a.f.status] - ranks[b.f.status]) * (sortReverse ? -1 : 1) ||
      a.p.file.localeCompare(b.p.file) ||
      a.f.rule_id.localeCompare(b.f.rule_id),
  );
  const visible = filtered.slice(0, limit);
  $("visible-count").textContent =
    `${visible.length} / ${filtered.length} constats affichés (${rows.length} dans le rapport complet).`;
  $("results").replaceChildren();
  for (const { p, f } of visible) {
    const tr = document.createElement("tr");
    tr.innerHTML = `<td><span class="status ${f.status}">${E(LABELS[f.status])}</span></td><td><strong>${E(p.file)}</strong><small>${E(f.label)} · ${E(f.rule_id)}</small></td><td>${E(f.reason)}</td><td><button class="text-button" type="button">Voir</button></td>`;
    const b = tr.querySelector("button")!;
    b.setAttribute("aria-label", `Voir la preuve : ${p.file}, ${f.label}`);
    b.addEventListener("click", () => openProof(p, f, b));
    $("results").append(tr);
  }
  if (!visible.length) {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = 4;
    td.textContent =
      "Aucun constat ne correspond à ce filtre. Consultez aussi les contrôles concordants.";
    tr.append(td);
    $("results").append(tr);
  }
  $("more-results").hidden = limit >= filtered.length;
}
function openProof(p: PageReport, f: Finding, button: HTMLButtonElement) {
  lastProofButton = button;
  $("proof").hidden = false;
  $("proof-title").textContent = `${p.file} — ${f.label}`;
  $("proof-reason").textContent = `${LABELS[f.status]} · ${f.reason}`;
  $("proof-html").textContent = JSON.stringify(
    {
      file: p.file,
      inputMode: p.inputMode,
      provenance: p.provenance,
      referenceUrl: p.referenceUrl,
      selector: f.selector,
      matches: f.matchCount,
      excerpt: f.htmlExcerpt,
      raw: f.htmlRaw,
      normalized: f.htmlNormalized,
    },
    null,
    2,
  );
  $("proof-json").textContent = JSON.stringify(
    {
      profile: p.profile,
      raw: f.jsonRaw,
      normalized: f.jsonNormalized,
      provenance: f.provenance,
      comparison: f.comparison,
    },
    null,
    2,
  );
  $("proof-limit").textContent = f.limit;
  $("proof-next").textContent = f.nextCheck;
  $("proof-title").focus();
}
for (const s of scenarios) {
  const option = document.createElement("option");
  option.value = s.id;
  option.textContent = `${s.id} · ${s.name}`;
  $("scenario").append(option);
}
$("scenario").addEventListener("change", () => {
  void renderScenario(value("scenario"));
});
$("apply-scenario").addEventListener("click", () => {
  if (currentScenario.info.id === "S0") {
    void renderScenario(replayId);
  } else {
    replayId = currentScenario.info.id;
    void renderScenario("S0");
  }
});
$("before").addEventListener("click", () => {
  void renderScenario("S0");
});
$("source-view").addEventListener("change", sourceView);
for (const id of [
  "profile-id",
  "timezone",
  "marker",
  "page-url-selector",
  "entity-by",
  "entity-id",
])
  $(id).addEventListener("input", formChange);
$("rule-fields").addEventListener("input", (event) => {
  const el = event.target as HTMLInputElement;
  if (!el.dataset.rule) return;
  const r = profile.rules[Number(el.dataset.rule)] as any;
  const [parent, key] = el.dataset.field!.split(".");
  if (key) {
    r[parent] ||= {};
    r[parent][key] = el.value;
    if (parent === "entity") r.entity.by = "id";
  } else r[parent] = el.value;
  syncJson();
  invalidate();
});
$("profile-json").addEventListener("input", () => {
  profileDirty = true;
  invalidate("Le JSON a été modifié. Appliquez-le avant une nouvelle analyse.");
  $<HTMLButtonElement>("analyze").disabled = true;
  $<HTMLButtonElement>("preview").disabled = true;
});
$("apply-profile").addEventListener("click", () => {
  try {
    profile = validateProfile(safeJSON(value("profile-json")));
    fillProfile();
    invalidate("Profil JSON appliqué. Prévisualisez ou relancez le contrôle.");
    $<HTMLButtonElement>("preview").disabled = false;
  } catch (e: any) {
    showError(e.message);
  }
});
$("profile-file").addEventListener("change", async () => {
  try {
    const file = $<HTMLInputElement>("profile-file").files?.[0];
    if (!file) return;
    profileDirty = true;
    invalidate();
    if (file.size > 100_000) throw new Error("Profil limité à 100 ko.");
    profile = validateProfile(safeJSON(await file.text()));
    fillProfile();
    $<HTMLButtonElement>("preview").disabled = false;
    announce("Profil importé ; les résultats précédents sont invalidés.");
  } catch (e: any) {
    showError(e.message);
  }
});
$("files").addEventListener("change", async () => {
  const epoch = ++ingestEpoch;
  invalidate();
  inputs = [];
  loading = true;
  updateFiles();
  try {
    const files = Array.from($<HTMLInputElement>("files").files || []);
    if (!files.length) return;
    if (
      files.length > LIMITS.files ||
      files.some((f) => f.size > LIMITS.fileBytes) ||
      files.reduce((s, f) => s + f.size, 0) > LIMITS.totalBytes
    )
      throw new Error(
        "Limites d’import dépassées (500 fichiers, 2 Mo chacun, 30 Mo au total).",
      );
    if (files.some((f) => !/\.html?$/i.test(f.name)))
      throw new Error("Fichiers .html ou .htm attendus.");
    const loaded = await Promise.all(
      files.map(async (f) => ({
        name: f.name,
        html: await f.text(),
        mode: "html_source" as const,
        provenance: "Fichier importé par l’utilisateur",
      })),
    );
    if (epoch !== ingestEpoch) return;
    inputs = loaded;
    updateFiles();
    announce("Fichiers chargés localement. Prévisualisez le témoin.");
  } catch (e: any) {
    inputs = [];
    updateFiles();
    showError(e.message);
  } finally {
    if (epoch === ingestEpoch) {
      loading = false;
      updateAnalyze();
    }
  }
});
for (const id of ["input-mode", "reference-url", "captured-at", "provenance"])
  $(id).addEventListener("input", () => invalidate());
$("load-example").addEventListener("click", () => {
  ingestEpoch++;
  loading = false;
  invalidate();
  inputs = [scenario("S1").inputs[0]];
  profile = structuredClone(demoProfile);
  fillProfile();
  updateFiles();
  announce(
    "Page fictive chargée avec son profil. Prévisualisez les extractions.",
  );
});
$("load-corpus").addEventListener("click", () => {
  ingestEpoch++;
  loading = false;
  invalidate();
  inputs = corpusInputs();
  profile = structuredClone(demoProfile);
  fillProfile();
  $<HTMLInputElement>("reference-url").value = "";
  updateFiles();
  announce(
    "200 pages fictives chargées. Lancez l’analyse pour retrouver les mutations.",
  );
});
$("preview").addEventListener("click", () => run("preview"));
$("analyze").addEventListener("click", () => run("analyze"));
$("cancel").addEventListener("click", () => {
  job++;
  worker.terminate();
  active = false;
  startWorker();
  setRunning(false);
  invalidate(
    "Analyse annulée. Aucun résultat partiel n’est présenté comme terminé.",
  );
});
$("download-profile").addEventListener("click", () => {
  try {
    validateProfile(profile);
    download(
      `${profile.id}.profile.json`,
      JSON.stringify(profile, null, 2),
      "application/json",
    );
  } catch (e: any) {
    showError(e.message);
  }
});
$("export-json").addEventListener("click", () => {
  if (report && !stale)
    download(
      "concordance-report.json",
      JSON.stringify(report, null, 2),
      "application/json",
    );
});
$("export-csv").addEventListener("click", () => {
  if (report && !stale)
    download(
      "concordance-findings.csv",
      csv(report, value("delimiter")),
      "text/csv;charset=utf-8",
    );
});
$("filter").addEventListener("change", () => {
  limit = 100;
  renderRows();
});
$("search-file").addEventListener("input", () => {
  limit = 100;
  renderRows();
});
$("more-results").addEventListener("click", () => {
  limit += 100;
  renderRows();
});
$("sort").addEventListener("click", () => {
  sortReverse = !sortReverse;
  $("sort")
    .closest("th")!
    .setAttribute("aria-sort", sortReverse ? "descending" : "ascending");
  renderRows();
  announce(`Tri par priorité ${sortReverse ? "inversé" : "croissant"}.`);
});
$("close-proof").addEventListener("click", () => {
  $("proof").hidden = true;
  lastProofButton?.focus();
});
$("reset").addEventListener("click", () => {
  ingestEpoch++;
  loading = false;
  profileDirty = false;
  lastProofButton = null;
  if (active) {
    job++;
    worker.terminate();
    active = false;
    startWorker();
  }
  inputs = [];
  report = null;
  profile = structuredClone(demoProfile);
  stale = false;
  for (const url of objectUrls) URL.revokeObjectURL(url);
  objectUrls.clear();
  for (const id of [
    "files",
    "profile-file",
    "reference-url",
    "captured-at",
    "search-file",
  ])
    $<HTMLInputElement>(id).value = "";
  $<HTMLSelectElement>("input-mode").value = "html_source";
  $<HTMLInputElement>("provenance").value = "Fichier importé par l’utilisateur";
  $("report").hidden = true;
  $("preview-result").hidden = true;
  $("preview-values").replaceChildren();
  $("results").replaceChildren();
  $("proof-html").textContent = "";
  $("proof-json").textContent = "";
  for (const id of [
    "report-summary",
    "visible-count",
    "proof-title",
    "proof-reason",
    "proof-limit",
    "proof-next",
  ])
    $(id).textContent = "";
  clearError();
  fillProfile();
  updateFiles();
  setRunning(false);
  $<HTMLButtonElement>("preview").disabled = false;
  announce("Réinitialisé. Fichiers et rapports retirés de l’application.");
});
window.addEventListener("pagehide", () => {
  worker?.terminate();
  for (const u of objectUrls) URL.revokeObjectURL(u);
});
for (const n of document.querySelectorAll<HTMLElement>(".interactive"))
  n.hidden = false;
document.querySelector(".reference")?.removeAttribute("open");
fillProfile();
startWorker();
const publicId = new URLSearchParams(location.search).get("scenario");
void renderScenario(
  scenarios.some((s) => s.id === publicId) ? publicId! : "S1",
);
